/*
 * Altisafe Pro — comportements de la page d'accueil.
 *
 * Portage de la logique de la maquette Claude Design :
 *   - reveal()   apparition au défilement, décalée de 70 ms par data-as-stagger
 *   - counters() comptage animé sur 900 ms, courbe cubic-out
 *   - accordion() FAQ, une seule réponse ouverte à la fois
 *
 * Le survol des cartes est passé en CSS pur (voir snippets/altisafe-tokens.liquid).
 *
 * Tout est initialisé par section afin de survivre aux rechargements AJAX de
 * l'éditeur de thème, et pour que deux instances d'une même section ne se
 * marchent pas dessus.
 */
(function () {
  'use strict';

  var reduced =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* Un observateur par racine, pour pouvoir tout débrancher au déchargement. */
  var registry = new Map();

  function cleanup(root) {
    var entry = registry.get(root);
    if (!entry) return;
    /* Remettre le DOM en état avant de couper les abonnements : sinon un
       bandeau déchargé en cours de rotation garderait ses items masqués. */
    entry.stoppers.forEach(function (fn) {
      try {
        fn();
      } catch (e) {}
    });
    entry.observers.forEach(function (o) {
      o.disconnect();
    });
    /* Le même identifiant sert aux délais et aux intervalles : les deux
       fonctions visent la même liste, on appelle les deux pour être explicite. */
    entry.timers.forEach(function (t) {
      clearTimeout(t);
      clearInterval(t);
    });
    entry.listeners.forEach(function (l) {
      l.el.removeEventListener(l.type, l.fn);
    });
    entry.media.forEach(function (m) {
      if (m.mql.removeEventListener) m.mql.removeEventListener('change', m.fn);
      else if (m.mql.removeListener) m.mql.removeListener(m.fn);
    });
    registry.delete(root);
  }

  function track(root) {
    var entry = registry.get(root);
    if (!entry) {
      entry = {
        observers: [],
        timers: [],
        listeners: [],
        media: [],
        stoppers: [],
      };
      registry.set(root, entry);
    }
    return entry;
  }

  /* ---- Apparition au défilement ------------------------------------- */
  function reveal(root, entry) {
    var els = root.querySelectorAll('[data-as-reveal]');
    if (!els.length) return;

    if (reduced || !window.IntersectionObserver) {
      els.forEach(function (el) {
        el.classList.add('as-in');
      });
      return;
    }

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (e) {
          if (!e.isIntersecting) return;
          e.target.classList.add('as-in');
          io.unobserve(e.target);
        });
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.1 }
    );

    els.forEach(function (el) {
      io.observe(el);
    });
    entry.observers.push(io);

    /* Filet de sécurité : si l'observateur ne se déclenche jamais
       (section masquée puis affichée, onglet en arrière-plan…), on montre tout. */
    entry.timers.push(
      setTimeout(function () {
        els.forEach(function (el) {
          el.classList.add('as-in');
        });
      }, 1500)
    );
  }

  /* ---- Compteurs ----------------------------------------------------- */
  function counters(root, entry) {
    var els = root.querySelectorAll('[data-as-count]');
    if (!els.length) return;

    function run(el) {
      var target = parseInt(el.getAttribute('data-as-count'), 10) || 0;
      if (reduced) {
        el.textContent = String(target);
        return;
      }
      var duration = 900;
      var start = null;
      function tick(now) {
        if (start === null) start = now;
        var k = Math.min(1, (now - start) / duration);
        el.textContent = String(Math.round(target * (1 - Math.pow(1 - k, 3))));
        if (k < 1) requestAnimationFrame(tick);
      }
      requestAnimationFrame(tick);
    }

    if (!window.IntersectionObserver) {
      els.forEach(run);
      return;
    }

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (e) {
          if (!e.isIntersecting) return;
          run(e.target);
          io.unobserve(e.target);
        });
      },
      { threshold: 0.4 }
    );

    els.forEach(function (el) {
      io.observe(el);
    });
    entry.observers.push(io);
  }

  /* ---- Accordéon FAQ -------------------------------------------------- */
  function accordion(root, entry) {
    var groups = root.querySelectorAll('[data-as-accordion]');

    groups.forEach(function (group) {
      var items = Array.prototype.slice.call(group.querySelectorAll('[data-as-item]'));
      if (!items.length) return;

      function close(item) {
        var btn = item.querySelector('[data-as-q]');
        var panel = item.querySelector('[data-as-a]');
        if (!btn || !panel) return;
        panel.style.height = '0px';
        panel.style.opacity = '0';
        btn.setAttribute('aria-expanded', 'false');
        item.removeAttribute('data-as-open');
      }

      function open(item) {
        var btn = item.querySelector('[data-as-q]');
        var panel = item.querySelector('[data-as-a]');
        if (!btn || !panel) return;
        var inner = panel.firstElementChild;
        panel.style.height = (inner ? inner.offsetHeight : 90) + 'px';
        panel.style.opacity = '1';
        btn.setAttribute('aria-expanded', 'true');
        item.setAttribute('data-as-open', '');
      }

      items.forEach(function (item) {
        var btn = item.querySelector('[data-as-q]');
        var panel = item.querySelector('[data-as-a]');
        if (!btn || !panel) return;

        panel.style.height = '0px';
        panel.style.opacity = '0';
        if (!reduced) {
          panel.style.transition = 'height .32s ease, opacity .28s ease';
        }

        var onClick = function () {
          var isOpen = item.hasAttribute('data-as-open');
          items.forEach(close);
          if (!isOpen) open(item);
        };
        btn.addEventListener('click', onClick);
        entry.listeners.push({ el: btn, type: 'click', fn: onClick });
      });

      /* La maquette ouvre la première question au chargement. */
      open(items[0]);

      /* Une réponse ouverte doit suivre les changements de largeur. */
      if (window.ResizeObserver) {
        var ro = new ResizeObserver(function () {
          var current = group.querySelector('[data-as-open]');
          if (current) open(current);
        });
        ro.observe(group);
        entry.observers.push(ro);
      }
    });
  }

  /* ---- Défilement des bandeaux sur mobile ----------------------------- */
  /*
   * Sur mobile, empiler 3 ou 4 promesses les unes sous les autres mange
   * l'écran. On les fait défiler l'une après l'autre à la place.
   *
   * Contrat :
   *   conteneur  [data-as-rotate]                  (+ data-as-rotate-interval en ms)
   *   items      [data-as-rotate-item]             (enfants directs)
   *   actif      classe .as-rot-on
   *   marqueur   l'attribut data-as-rotate-active est posé sur le conteneur
   *              UNIQUEMENT quand la rotation tourne réellement.
   *
   * Le CSS ne bascule en mode « scène » que sur ce marqueur : sans JS, avec
   * un seul item, ou si l'utilisateur refuse les animations, les items
   * restent simplement empilés — le contenu ne disparaît jamais.
   */
  function rotator(root, entry) {
    var boxes = root.querySelectorAll('[data-as-rotate]');
    if (!boxes.length) return;

    var mql = window.matchMedia('(max-width: 749px)');

    boxes.forEach(function (box) {
      var items = Array.prototype.slice.call(
        box.querySelectorAll('[data-as-rotate-item]')
      );
      if (items.length < 2) return;

      var delay = parseInt(box.getAttribute('data-as-rotate-interval'), 10) || 3800;
      var index = 0;
      var timer = null;

      function paint() {
        items.forEach(function (el, i) {
          el.classList.toggle('as-rot-on', i === index);
          /* Les items masqués sortent du parcours au clavier et du fil
             de lecture des lecteurs d'écran. */
          el.setAttribute('aria-hidden', i === index ? 'false' : 'true');
        });
      }

      function tick() {
        index = (index + 1) % items.length;
        paint();
      }

      function start() {
        if (timer) return;
        box.setAttribute('data-as-rotate-active', '');
        index = 0;
        paint();
        timer = setInterval(tick, delay);
        entry.timers.push(timer);
      }

      function stop() {
        if (timer) {
          clearInterval(timer);
          timer = null;
        }
        box.removeAttribute('data-as-rotate-active');
        items.forEach(function (el) {
          el.classList.remove('as-rot-on');
          el.removeAttribute('aria-hidden');
        });
      }

      function apply() {
        /* Pas d'animation imposée à qui n'en veut pas : on retombe sur
           l'empilement, qui reste lisible. */
        if (mql.matches && !reduced) start();
        else stop();
      }

      apply();

      var onChange = function () {
        apply();
      };
      /* addEventListener sur un MediaQueryList : Safari < 14 ne l'a pas. */
      if (mql.addEventListener) mql.addEventListener('change', onChange);
      else if (mql.addListener) mql.addListener(onChange);
      entry.media.push({ mql: mql, fn: onChange });

      /* Inutile de faire défiler un onglet que personne ne regarde. */
      var onVisibility = function () {
        if (document.hidden) {
          if (timer) {
            clearInterval(timer);
            timer = null;
          }
        } else if (mql.matches && !reduced && !timer) {
          timer = setInterval(tick, delay);
          entry.timers.push(timer);
        }
      };
      document.addEventListener('visibilitychange', onVisibility);
      entry.listeners.push({
        el: document,
        type: 'visibilitychange',
        fn: onVisibility,
      });

      entry.stoppers.push(stop);
    });
  }

  /* ---- Amorçage -------------------------------------------------------- */
  function init(root) {
    if (!root) return;
    cleanup(root);
    var entry = track(root);
    reveal(root, entry);
    counters(root, entry);
    accordion(root, entry);
    rotator(root, entry);
  }

  function initAll() {
    document.querySelectorAll('[data-as-section]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  /* Éditeur de thème : une section rechargée doit être réamorcée, et une
     section retirée doit libérer ses observateurs. */
  document.addEventListener('shopify:section:load', function (e) {
    var root = e.target.querySelector('[data-as-section]');
    if (root) init(root);
  });

  document.addEventListener('shopify:section:unload', function (e) {
    var root = e.target.querySelector('[data-as-section]');
    if (root) cleanup(root);
  });
})();
